import axiosInstance from "@/api/axiosInstance";
import { ENDPOINTS } from "@/constants/api";
import { requestHandler } from "@/helpers/requestHandler";
import { withErrorHandling } from "@/utils/withErrorHandling";

/** A wind station as returned by /windystations/bbox and /nearest. */
export interface WindStation {
  id: string;
  name: string | null;
  lat: number;
  lon: number;
  /** "pws" | "metar" | "ndbc" | "knmi" | "synthetic" */
  source: string;
  windKts?: number;
  windDir?: number;
  gustKts?: number;
  /** observation_ts (epoch ms) */
  updatedAt?: number;
  /** observation older than 1 h */
  stale?: boolean;
  /** row updated_at (epoch ms) */
  fetchedAt?: number;
  distanceKm?: number;
}

/** /windystations/search result (lighter shape). */
export interface WindStationSearchResult {
  id: string;
  name: string | null;
  lat: number;
  lon: number;
  source: string;
  isOnline: boolean;
}

export interface WindStationHistoryPoint {
  ts: number;
  windAvg: number;
  windMax: number;
  windDir: number;
}

export interface WindStationHistory {
  station: { id: string; name: string | null; lat: number; lon: number };
  series: WindStationHistoryPoint[];
}

// A whole-world bbox — the classic endpoint caps results with `limit`.
const WORLD = { minlat: -90, maxlat: 90, minlon: -180, maxlon: 180 };

export const WindStationService = {
  /**
   * Stations within a bounding box (with live wind values). Defaults to the whole
   * world so the admin list can show everything up to `limit`.
   */
  async list(
    limit = 500,
    bbox: { minlat: number; maxlat: number; minlon: number; maxlon: number } = WORLD
  ): Promise<WindStation[]> {
    return withErrorHandling(async () => {
      const response = await axiosInstance.get(ENDPOINTS.WIND_STATIONS.BBOX, {
        params: { ...bbox, limit },
      });
      return (response.data as WindStation[]) || [];
    }, "WindStationService -> list");
  },

  /** Name/ID search (server-side ILIKE). */
  async search(q: string, limit = 25): Promise<WindStationSearchResult[]> {
    return withErrorHandling(async () => {
      const response = await axiosInstance.get(ENDPOINTS.WIND_STATIONS.SEARCH, {
        params: { q, limit },
      });
      return (response.data as WindStationSearchResult[]) || [];
    }, "WindStationService -> search");
  },

  /** Nearest N stations to a point, with live values + distance. */
  async nearest(lat: number, lon: number, limit = 5): Promise<WindStation[]> {
    return withErrorHandling(async () => {
      const response = await axiosInstance.get(ENDPOINTS.WIND_STATIONS.NEAREST, {
        params: { lat, lon, limit },
      });
      return (response.data as WindStation[]) || [];
    }, "WindStationService -> nearest");
  },

  async history(id: string): Promise<WindStationHistory> {
    return withErrorHandling(async () => {
      const response = await axiosInstance.get(ENDPOINTS.WIND_STATIONS.HISTORY(id));
      return response.data as WindStationHistory;
    }, "WindStationService -> history");
  },

  /** Force-refresh a PWS station's observation (PWS only; others are scraped). */
  async refresh(id: string): Promise<WindStation> {
    return requestHandler<WindStation>(async () => {
      const response = await axiosInstance.post(ENDPOINTS.WIND_STATIONS.REFRESH(id));
      return response.data as WindStation;
    }, "WindStationService -> refresh");
  },
};
